/** Print a kitchen ticket ("comanda") for an order.
 * Opens a new window with 80mm receipt format optimized for thermal printers.
 * Falls back to A4/letter print on normal printers.
 */

const currency = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v ?? 0);

const fmtDT = (v) => {
  if (!v) return '';
  const d = typeof v === 'string' ? new Date(v) : v;
  return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const PAYMENT = { cash: 'CONTANTI', card: 'CARTA' };

export function printOrder(order) {
  const scheduledLine = order.scheduled_for
    ? `<div class="pre-order">⏰ PRE-ORDINE: ${fmtDT(order.scheduled_for)}</div>`
    : '';

  const itemsHtml = (order.items || []).map((it, i) => {
    const comps = (it.components_summary || []).filter(Boolean).map(c => `<div class="comp">${escapeHtml(c)}</div>`).join('');
    const notes = it.notes ? `<div class="notes">📝 ${escapeHtml(it.notes)}</div>` : '';
    return `
      <div class="item">
        <div class="item-row">
          <span class="qty">${it.quantity}×</span>
          <span class="name">${escapeHtml(it.name)}</span>
          <span class="price">${currency(it.unit_price * it.quantity)}</span>
        </div>
        ${comps}
        ${notes}
      </div>
    `;
  }).join('');

  const deliveryNotes = order.delivery_notes ? `<div class="section-title">Note consegna</div><div class="section-body">${escapeHtml(order.delivery_notes)}</div>` : '';

  const html = `
<!doctype html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>Comanda #${order.order_number}</title>
  <style>
    * { box-sizing: border-box; }
    body {
      margin: 0;
      padding: 8px;
      font-family: 'Menlo', 'Courier New', monospace;
      font-size: 12px;
      color: #000;
      background: #fff;
    }
    .receipt {
      width: 80mm;
      margin: 0 auto;
      padding: 4mm;
    }
    .brand {
      text-align: center;
      font-weight: bold;
      font-size: 18px;
      margin-bottom: 2mm;
      letter-spacing: 2px;
    }
    .brand small { display: block; font-weight: normal; font-size: 11px; letter-spacing: normal; margin-top: 1mm; }
    .divider { border-top: 1.5px dashed #000; margin: 3mm 0; }
    .divider-solid { border-top: 2px solid #000; margin: 3mm 0; }
    .order-header { text-align: center; }
    .order-header .num { font-size: 22px; font-weight: bold; margin: 2mm 0; }
    .order-header .datetime { font-size: 11px; }
    .pre-order {
      background: #FFF3B0;
      border: 2px solid #000;
      padding: 3mm;
      text-align: center;
      font-weight: bold;
      font-size: 13px;
      margin: 3mm 0;
    }
    .section-title {
      font-weight: bold;
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 1px;
      margin-top: 3mm;
      padding-bottom: 1mm;
      border-bottom: 1px solid #000;
    }
    .section-body {
      font-size: 12px;
      margin-top: 1mm;
      line-height: 1.4;
    }
    .item {
      margin: 3mm 0;
      padding-bottom: 2mm;
      border-bottom: 1px dotted #000;
    }
    .item:last-child { border-bottom: none; }
    .item-row {
      display: flex;
      justify-content: space-between;
      font-weight: bold;
      font-size: 14px;
    }
    .item-row .qty { min-width: 8mm; }
    .item-row .name { flex: 1; padding: 0 2mm; }
    .item-row .price { min-width: 18mm; text-align: right; }
    .comp {
      font-size: 11px;
      margin-left: 10mm;
      padding: 0.5mm 0;
    }
    .comp::before { content: "› "; font-weight: bold; }
    .notes {
      font-size: 11px;
      background: #EEE;
      padding: 1mm 2mm;
      margin: 1mm 0 1mm 10mm;
      font-weight: bold;
    }
    .totals { margin-top: 3mm; }
    .totals-row { display: flex; justify-content: space-between; padding: 0.5mm 0; font-size: 12px; }
    .totals-row.total { font-size: 15px; font-weight: bold; padding-top: 2mm; border-top: 2px solid #000; margin-top: 2mm; }
    .payment {
      text-align: center;
      font-weight: bold;
      font-size: 13px;
      background: #000;
      color: #fff;
      padding: 2mm;
      margin-top: 3mm;
      letter-spacing: 2px;
    }
    .footer {
      text-align: center;
      margin-top: 4mm;
      font-size: 10px;
      color: #333;
    }
    @page { size: 80mm auto; margin: 0; }
    @media screen {
      body { background: #F0F0F0; padding: 20px; }
      .receipt { background: #fff; box-shadow: 0 4px 16px rgba(0,0,0,0.15); }
    }
    @media print {
      body { background: #fff; padding: 0; }
      .receipt { box-shadow: none; }
      .no-print { display: none; }
    }
    .actions { text-align: center; margin: 4mm 0; }
    .btn {
      background: #1E4D9B; color: #fff; border: 0;
      padding: 8px 20px; border-radius: 8px;
      font-size: 13px; font-weight: bold;
      cursor: pointer; margin: 0 4px;
    }
    .btn.secondary { background: #666; }
  </style>
</head>
<body>
  <div class="receipt">
    <div class="brand">MōMA Pokè<small>Corso delle Province 169A · Catania</small></div>
    <div class="divider-solid"></div>

    <div class="order-header">
      <div class="datetime">${fmtDT(order.created_at)}</div>
      <div class="num">#${order.order_number}</div>
    </div>

    ${scheduledLine}

    <div class="section-title">Cliente</div>
    <div class="section-body">
      <b>${escapeHtml(order.customer_name || '')}</b><br/>
      ${escapeHtml(order.customer_phone || '')}
    </div>

    <div class="section-title">Consegna</div>
    <div class="section-body">
      ${escapeHtml(order.delivery_address || '')}<br/>
      Zona: <b>${escapeHtml(order.zone_name || '—')}</b>
    </div>

    ${deliveryNotes}

    <div class="section-title">Ordine</div>
    ${itemsHtml}

    <div class="totals">
      <div class="totals-row"><span>Subtotale</span><span>${currency(order.subtotal)}</span></div>
      <div class="totals-row"><span>Consegna</span><span>${currency(order.delivery_fee)}</span></div>
      <div class="totals-row total"><span>TOTALE</span><span>${currency(order.total)}</span></div>
    </div>

    <div class="payment">${PAYMENT[order.payment_method] || 'PAGAMENTO'}</div>

    ${order.assigned_rider_name ? `<div class="section-title">Rider</div><div class="section-body">🛵 ${escapeHtml(order.assigned_rider_name)}</div>` : ''}

    <div class="footer">
      Grazie e buon appetito!<br/>
      instagram.com/moma_poke · tiktok.com/@moma_poke
    </div>

    <div class="actions no-print">
      <button class="btn" onclick="window.print()">🖨️ Stampa</button>
      <button class="btn secondary" onclick="window.close()">Chiudi</button>
    </div>
  </div>
  <script>
    // Auto-print on load (comment out if you prefer manual)
    window.addEventListener('load', () => {
      setTimeout(() => window.print(), 300);
    });
  </script>
</body>
</html>
`;

  const win = window.open('', '_blank', 'width=380,height=800');
  if (!win) {
    alert('Il browser ha bloccato la finestra di stampa. Consenti popup per questo sito.');
    return;
  }
  win.document.open();
  win.document.write(html);
  win.document.close();
}

function escapeHtml(s) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
